import type { MealSlot } from '@cadencia/contracts';
import { requireFood, type ProteinSource } from './foods.js';

/**
 * Catalogo de recetas. En codigo y referenciado por `slug`, igual que los
 * alimentos (ADR-036). Cocina espanola y mediterranea.
 *
 * Las cantidades son **por racion y en crudo**, y por eso `servings` es siempre
 * 1: el generador escala gramos, no raciones. Una receta «para 4» obligaria a
 * dividir en todas partes y a decidir que hacer con el resto, que es una fuente
 * de errores a cambio de nada.
 *
 * Los macros NO se guardan: se calculan desde los ingredientes con
 * `recipeMacros`. Un total almacenado se queda viejo en cuanto alguien toca un
 * gramo.
 */

/**
 * Version del catalogo. Sube cuando cambia lo que el catalogo PROMETE, no cuando
 * se toca una receta.
 *
 * 2 (08/09/2026): las recetas pasan a tener `description` y `steps` en vez de
 * una sola cadena `instructions`, y entran seis tentempies nuevos para que la
 * merienda tenga con que.
 *
 * OJO: hoy no la lee nadie. Se mantiene porque una receta guardada en un
 * `recipe_snapshot` puede necesitar saber contra que catalogo se genero, pero
 * mientras eso no exista esto es una constante y no un mecanismo.
 */
export const RECIPE_CATALOG_VERSION = 2;

export type RecipeTag =
  | 'spanish'
  | 'mediterranean'
  | 'high_protein'
  | 'quick'
  | 'vegetarian'
  | 'no_cook'
  | 'oven'
  | 'one_pot';

export interface RecipeIngredient {
  foodSlug: string;
  amount: number;
  unit: 'g' | 'ml' | 'unit';
  /** El aceite escala; la sal y las especias no (§5.2 paso 4). */
  isScalable: boolean;
  scaleMinPct: number;
  scaleMaxPct: number;
}

export interface Recipe {
  slug: string;
  /**
   * Nombre culinario. Tiene que corresponderse con lo que hay en
   * `ingredients` y con lo que dicen los `steps` (§24 del encargo): un plato
   * que se llama «con verduras asadas» tiene que llevar verdura y tiene que
   * asarla en algun paso.
   */
  nameEs: string;
  /** Una frase que dice que es el plato, para la tarjeta y el detalle. */
  description: string;
  /** Huecos del dia en los que encaja. */
  slots: readonly MealSlot[];
  servings: number;
  proteinSource: ProteinSource;
  tags: readonly RecipeTag[];
  minutes: number;
  /**
   * La elaboracion, en pasos ordenados y ejecutables (§25 del encargo).
   *
   * Sustituyo al campo `instructions`, que era una sola frase corrida con toda
   * la receta dentro. No era un problema de formato: en una frase no cabe el
   * orden real —que se pone a cocer antes, cuanto tiempo, cuando se incorpora
   * cada cosa— y por eso todas acababan diciendo lo mismo, «sofrie, anade y
   * cuece». Con pasos se puede exigir que haya mas de uno y que ninguno sea
   * «mezclalo todo», que es justo lo que valida `validateGeneratedRecipe` en
   * las que propone la IA.
   */
  steps: readonly string[];
  ingredients: readonly RecipeIngredient[];
}

/** La elaboracion como texto corrido, para donde no caben los pasos. */
export function recipeInstructions(recipe: Recipe): string {
  return recipe.steps.join(' ');
}

interface IngredientOpts {
  unit?: 'g' | 'ml' | 'unit';
  /** Cantidad fija: condimentos y especias no escalan. */
  fixed?: boolean;
  min?: number;
  max?: number;
}

/**
 * Cuanto se puede mover la racion de un alimento, por defecto.
 *
 * No es lo mismo para todo, y tratarlo como si lo fuera le costaba al generador
 * los dias de mucha carga: con margenes iguales, subir los hidratos obligaba a
 * subir tambien el aceite, y un dia de hidratos altos con la grasa en su suelo
 * del 20 % no cerraba nunca. Los almidones se mueven mucho —nadie nota 130 g de
 * arroz en vez de 100— y las grasas se pueden recortar a la mitad cocinando con
 * menos aceite. La proteina se queda en el margen estrecho: es lo que da su
 * caracter al plato, y ademas es lo que cuesta dinero.
 *
 * El maximo de las grasas se deja en 1,4 a proposito, aunque no se use: el
 * factor UNIFORME de la receta es la interseccion de los margenes de sus
 * ingredientes (`recipeScaleBounds`), asi que bajarlo aqui estrecharia el
 * escalado del plato entero.
 */
function margenPorDefecto(foodSlug: string): { min: number; max: number } {
  const food = requireFood(foodSlug);
  if (food.fat100 >= 50) return { min: 0.5, max: 1.4 };
  if (['cereals', 'legumes', 'produce'].includes(food.section)) return { min: 0.6, max: 1.8 };
  return { min: 0.7, max: 1.4 };
}

const I = (foodSlug: string, amount: number, opts: IngredientOpts = {}): RecipeIngredient => {
  const margen = margenPorDefecto(foodSlug);
  return {
    foodSlug,
    amount,
    unit: opts.unit ?? 'g',
    isScalable: !(opts.fixed ?? false),
    scaleMinPct: opts.fixed ? 1 : (opts.min ?? margen.min),
    scaleMaxPct: opts.fixed ? 1 : (opts.max ?? margen.max),
  };
};

const R = (
  slug: string,
  nameEs: string,
  description: string,
  slots: readonly MealSlot[],
  proteinSource: ProteinSource,
  minutes: number,
  tags: readonly RecipeTag[],
  steps: readonly string[],
  ingredients: readonly RecipeIngredient[],
): Recipe => ({
  slug,
  nameEs,
  description,
  slots,
  servings: 1,
  proteinSource,
  tags,
  minutes,
  steps,
  ingredients,
});

export const RECIPES: readonly Recipe[] = [
  // ------------------------------------------------------------- desayunos
  R('avena-con-yogur-y-fruta', 'Avena cremosa con yogur, plátano y crema de cacahuete',
    'Un bol frío de avena con yogur proteico, plátano en rodajas y un hilo de crema de cacahuete.',
    ['breakfast'], 'dairy', 5,
    ['quick', 'high_protein', 'no_cook'],
    [
      'Pon los copos de avena en un bol y añade el yogur proteico. Remueve hasta que no queden copos secos.',
      'Corta el plátano en rodajas finas y repártelas por encima.',
      'Termina con la crema de cacahuete en hilo y espolvorea la canela.',
    ],
    [I('avena-copos', 60), I('yogur-proteico', 150), I('platano', 100), I('crema-cacahuete', 10), I('canela', 1, { fixed: true })]),

  R('tostadas-con-tomate-y-pavo', 'Tostadas de pan integral con tomate rallado y pavo',
    'El desayuno salado de toda la vida: pan tostado, tomate con aceite y unas lonchas de pavo.',
    ['breakfast'], 'poultry', 8,
    ['spanish', 'quick'],
    [
      'Tuesta las rebanadas de pan integral hasta que estén doradas por las dos caras.',
      'Ralla el tomate, escúrrelo un poco y extiéndelo sobre el pan. Riega con el aceite de oliva.',
      'Coloca las lonchas de pavo por encima y sirve enseguida, antes de que el pan se ablande.',
    ],
    [I('pan-integral', 80), I('tomate', 80), I('aceite-oliva-virgen-extra', 8, { unit: 'ml' }), I('pavo-fiambre', 60)]),

  R('tortilla-de-claras-con-pan', 'Tortilla jugosa de huevo y claras con pan tostado',
    'Una tortilla alta y jugosa de huevo entero y claras, con pan tostado para acompañar.',
    ['breakfast'], 'egg', 10,
    ['high_protein', 'quick'],
    [
      'Bate el huevo con las claras y una pizca de sal hasta que la mezcla quede homogénea y con algo de aire.',
      'Calienta el aceite en una sartén antiadherente a fuego medio y vierte la mezcla.',
      'Remueve los primeros segundos con una espátula y deja cuajar sin que se seque; dóblala por la mitad.',
      'Tuesta el pan y sírvelo al lado.',
    ],
    [I('huevo', 1, { unit: 'unit' }), I('clara-huevo', 120), I('pan-integral', 60), I('aceite-oliva-virgen-extra', 5, { unit: 'ml' })]),

  R('porridge-de-avena-y-cacao', 'Porridge caliente de avena y cacao con proteína',
    'Avena cocida con leche hasta quedar cremosa, con cacao puro, proteína y canela.',
    ['breakfast'], 'dairy', 8,
    ['high_protein'],
    [
      'Lleva la leche a fuego medio y añade los copos de avena en cuanto empiece a humear.',
      'Cuece cinco minutos sin dejar de remover, hasta que espese y se despegue del fondo.',
      'Retira del fuego y deja templar un minuto: si añades la proteína hirviendo, se corta.',
      'Incorpora el cacao puro, la proteína y la canela, y bate hasta que quede liso.',
    ],
    [I('avena-copos', 70), I('leche-semidesnatada', 250, { unit: 'ml' }), I('cacao-puro', 8), I('proteina-whey', 25), I('canela', 1, { fixed: true })]),

  R('yogur-griego-con-frutos-rojos', 'Yogur griego con frutos rojos, nueces y miel',
    'Yogur griego frío con frutos rojos, nueces troceadas y un hilo de miel.',
    ['breakfast', 'midmorning', 'afternoon_snack'], 'dairy', 4,
    ['quick', 'no_cook'],
    [
      'Saca los frutos rojos del congelador y deja que se descongelen unos minutos: soltarán algo de jugo, y ese jugo es la salsa.',
      'Pon el yogur griego en un bol y añade los frutos rojos con su jugo.',
      'Trocea las nueces con las manos y espárcelas por encima. Termina con la miel.',
    ],
    [I('yogur-griego', 200), I('frutos-rojos-congelados', 100), I('nuez', 20), I('miel', 10)]),

  R('revuelto-de-huevo-y-espinacas', 'Revuelto cremoso de huevo y espinacas',
    'Espinacas salteadas y huevo cuajado a fuego suave, con pan para mojar.',
    ['breakfast', 'dinner'], 'egg', 10,
    ['quick', 'high_protein'],
    [
      'Calienta el aceite y saltea las espinacas a fuego fuerte hasta que pierdan el agua.',
      'Baja el fuego, añade el huevo batido y remueve en el centro de la sartén.',
      'Retira cuando esté cuajado pero todavía brillante: con el calor residual termina de hacerse.',
      'Tuesta el pan y sírvelo al lado.',
    ],
    [I('huevo', 2, { unit: 'unit' }), I('espinaca', 80), I('aceite-oliva-virgen-extra', 8, { unit: 'ml' }), I('pan-integral', 60)]),

  R('tostada-de-aguacate-y-huevo', 'Tostada de aguacate machacado con huevo a la plancha',
    'Pan tostado con aguacate machacado y un huevo a la plancha con la yema entera.',
    ['breakfast'], 'egg', 10,
    ['quick', 'mediterranean'],
    [
      'Tuesta el pan hasta que aguante el peso del aguacate sin doblarse.',
      'Machaca el aguacate con un tenedor y la sal, y extiéndelo sobre el pan.',
      'Cuaja el huevo a la plancha a fuego medio, con la clara hecha y la yema líquida, y colócalo encima.',
    ],
    [I('pan-integral', 70), I('aguacate', 60), I('huevo', 1, { unit: 'unit' }), I('sal', 1, { fixed: true })]),

  R('batido-de-platano-y-avena', 'Batido de plátano, avena y proteína',
    'Un batido espeso de plátano y avena, para los desayunos en los que no apetece masticar.',
    ['breakfast', 'midmorning', 'afternoon_snack'], 'dairy', 4,
    ['quick', 'high_protein', 'no_cook'],
    [
      'Pon la leche en el vaso de la batidora y añade encima la avena: así no se pega al fondo.',
      'Añade el plátano troceado, la proteína y la crema de cacahuete.',
      'Tritura un minuto largo, hasta que la avena deje de notarse y el batido quede cremoso.',
    ],
    [I('leche-desnatada', 300, { unit: 'ml' }), I('platano', 120), I('avena-copos', 40), I('proteina-whey', 30), I('crema-cacahuete', 10)]),

  R('requeson-con-miel-y-nueces', 'Requesón con manzana, nueces y miel',
    'Requesón fresco con manzana en dados, nueces y un hilo de miel.',
    ['breakfast', 'midmorning', 'afternoon_snack'], 'dairy', 4,
    ['quick', 'no_cook'],
    [
      'Corta la manzana en dados pequeños, con piel, y mézclalos con el requesón.',
      'Trocea las nueces y añádelas por encima.',
      'Termina con la miel en hilo justo antes de comer, para que no se disuelva.',
    ],
    [I('requeson', 200), I('miel', 12), I('nuez', 20), I('manzana', 150)]),

  R('tostadas-con-salmon-y-queso-crema', 'Tostadas de centeno con salmón ahumado, queso crema y rúcula',
    'Pan de centeno con queso crema, salmón ahumado y unas hojas de rúcula.',
    ['breakfast'], 'fish', 6,
    ['quick', 'high_protein'],
    [
      'Tuesta el pan de centeno ligeramente: lo justo para que quede firme, sin secarse.',
      'Unta el queso crema en caliente, que se extiende mucho mejor.',
      'Coloca el salmón ahumado en pliegues, no estirado, y termina con la rúcula.',
    ],
    [I('pan-centeno', 70), I('salmon-ahumado', 60), I('queso-crema-light', 30), I('rucula', 20)]),

  // --------------------------------------------------- media mañana y merienda
  R('yogur-proteico-con-almendras', 'Yogur proteico con almendras troceadas',
    'Dos ingredientes y treinta segundos: yogur proteico con almendras troceadas.',
    ['midmorning', 'afternoon_snack', 'extra'], 'dairy', 2,
    ['quick', 'no_cook', 'high_protein'],
    [
      'Trocea las almendras en dos o tres partes cada una: enteras se comen aparte, troceadas se comen con el yogur.',
      'Ponlas sobre el yogur justo antes de comer para que no se ablanden.',
    ],
    [I('yogur-proteico', 150), I('almendra', 20)]),

  R('fruta-y-frutos-secos', 'Manzana con almendras',
    'La merienda más simple que existe: una pieza de fruta y un puñado de almendras.',
    ['midmorning', 'afternoon_snack', 'extra'], 'none', 2,
    ['quick', 'no_cook'],
    [
      'Corta la manzana en gajos y retira el corazón.',
      'Acompáñala con las almendras, alternando bocado de fruta y fruto seco.',
    ],
    [I('manzana', 180), I('almendra', 25)]),

  R('bocadillo-de-atun-y-tomate', 'Bocadillo de atún con tomate rallado',
    'Un bocadillo de pan integral con tomate rallado al aceite y atún al natural escurrido.',
    ['midmorning', 'afternoon_snack', 'lunch'], 'fish', 6,
    ['spanish', 'quick', 'high_protein'],
    [
      'Abre el pan por la mitad sin llegar a separarlo.',
      'Ralla el tomate, mézclalo con el aceite y una pizca de sal, y extiéndelo sobre las dos caras.',
      'Escurre bien el atún y repártelo. Cierra y presiona para que no se desmonte al llevarlo.',
    ],
    [I('pan-integral', 80), I('atun-lata-natural', 80), I('tomate', 60), I('aceite-oliva-virgen-extra', 5, { unit: 'ml' })]),

  R('queso-batido-con-frutos-rojos', 'Queso batido con arándanos y chía',
    'Queso batido con arándanos y semillas de chía, que espesan si lo dejas reposar.',
    ['midmorning', 'afternoon_snack', 'extra'], 'dairy', 3,
    ['quick', 'no_cook', 'high_protein'],
    [
      'Mezcla el queso batido con las semillas de chía y deja reposar cinco minutos: la chía absorbe líquido y lo deja cremoso.',
      'Añade los arándanos, removiendo con cuidado para que no se rompan.',
    ],
    [I('queso-batido-0', 200), I('arandanos', 100), I('semillas-chia', 10)]),

  R('bocadillo-de-pavo-y-queso', 'Bocadillo de pavo, queso y tomate',
    'Pan integral con pavo, queso semicurado y tomate en rodajas.',
    ['midmorning', 'afternoon_snack'], 'poultry', 5,
    ['spanish', 'quick'],
    [
      'Abre el pan y corta el tomate en rodajas finas; sálalas ligeramente.',
      'Monta el bocadillo con el queso pegado al pan y el pavo en el centro, para que el tomate no lo empape.',
    ],
    [I('pan-integral', 80), I('pavo-fiambre', 60), I('queso-semicurado', 25), I('tomate', 50)]),

  R('platano-y-crema-de-cacahuete', 'Plátano con crema de cacahuete',
    'Plátano en rodajas con crema de cacahuete: la merienda de antes de entrenar.',
    ['midmorning', 'afternoon_snack', 'extra'], 'none', 2,
    ['quick', 'no_cook'],
    [
      'Corta el plátano a lo largo o en rodajas gruesas.',
      'Reparte la crema de cacahuete por encima con una cucharilla.',
    ],
    [I('platano', 130), I('crema-cacahuete', 20)]),

  R('hummus-con-crudites', 'Hummus con crudités de zanahoria y pepino y pan de pita',
    'Bastones de zanahoria y pepino con hummus y pan de pita tostado para mojar.',
    ['midmorning', 'afternoon_snack', 'extra'], 'legume', 5,
    ['mediterranean', 'quick', 'vegetarian'],
    [
      'Corta la zanahoria y el pepino en bastones del mismo grosor, para que se mojen igual de bien.',
      'Tuesta el pan de pita hasta que quede crujiente y pártelo en triángulos.',
      'Sirve el hummus en un cuenco en el centro y las crudités alrededor.',
    ],
    [I('hummus', 100), I('zanahoria', 100), I('pepino', 100), I('pan-pita-integral', 60)]),

  R('tostada-de-requeson-y-miel', 'Tostadas de requesón y miel',
    'Tostadas integrales con requesón y un hilo de miel.',
    ['midmorning', 'afternoon_snack', 'extra'], 'dairy', 3,
    ['quick', 'no_cook'],
    [
      'Reparte el requesón sobre las tostadas y extiéndelo hasta los bordes.',
      'Añade la miel en hilo por encima justo antes de comer.',
    ],
    [I('tostadas-integrales', 40), I('requeson', 120), I('miel', 10)]),

  R('batido-de-proteina-y-fresas', 'Batido de fresas con proteína',
    'Leche, fresas y proteína batidas hasta quedar suave. Frío, mejor.',
    ['midmorning', 'afternoon_snack', 'extra'], 'dairy', 3,
    ['quick', 'no_cook', 'high_protein'],
    [
      'Limpia las fresas y quítales el rabito.',
      'Tritura la leche con la proteína primero, para que no queden grumos, y añade las fresas después.',
    ],
    [I('leche-desnatada', 250, { unit: 'ml' }), I('proteina-whey', 30), I('fresa', 150)]),

  R('mix-de-frutos-secos-y-pasas', 'Mezcla de almendras, nueces, pistachos y pasas',
    'Un puñado de frutos secos con pasas, para llevar encima.',
    ['midmorning', 'afternoon_snack', 'extra'], 'none', 1,
    ['quick', 'no_cook'],
    [
      'Mezcla los frutos secos con las pasas en un bote pequeño.',
      'Repártelo en la ración del día antes de salir: del bote grande siempre se come de más.',
    ],
    [I('almendra', 15), I('nuez', 10), I('pistacho', 15), I('pasas', 20)]),

  // Las seis siguientes entraron el 08/09/2026 con la merienda.
  //
  // NO SON RELLENO. Con el esquema configurable, quien hace cinco comidas
  // necesita CATORCE tentempies a la semana —media mañana y merienda, siete
  // dias— y la ventana de variedad del §5.4 no deja repetir receta en diez
  // dias: con trece disponibles, el generador se quedaba sin candidatas el
  // ultimo dia y el hueco salia VACIO. No fallaba nada; el dia se quedaba con
  // cuatro comidas y sin cerrar. Ademas son mas densas en hidratos que las que
  // habia, que es lo que pide una merienda pegada a un entrenamiento de tarde.
  R('bocadillo-de-jamon-y-tomate', 'Bocadillo de jamón serrano con tomate',
    'Pan integral con tomate al aceite y jamón serrano en lonchas finas.',
    ['midmorning', 'afternoon_snack', 'extra'], 'pork', 5,
    ['spanish', 'quick'],
    [
      'Abre el pan y ralla el tomate; mézclalo con el aceite y una pizca de sal.',
      'Extiende el tomate sobre las dos caras del pan y reparte el jamón sin amontonarlo.',
    ],
    [I('pan-integral', 90), I('jamon-serrano', 50), I('tomate', 60), I('aceite-oliva-virgen-extra', 5, { unit: 'ml' })]),

  R('tostada-de-cacahuete-y-platano', 'Tostada de crema de cacahuete con plátano',
    'Pan tostado con crema de cacahuete y plátano en rodajas, con canela.',
    ['midmorning', 'afternoon_snack', 'extra'], 'none', 4,
    ['quick', 'no_cook'],
    [
      'Tuesta el pan y extiende la crema de cacahuete mientras esté caliente, que se reparte mejor.',
      'Coloca el plátano en rodajas encima y espolvorea la canela.',
    ],
    [I('pan-integral', 70), I('crema-cacahuete', 20), I('platano', 110), I('canela', 1, { fixed: true })]),

  R('yogur-con-muesli-y-arandanos', 'Yogur proteico con muesli y arándanos',
    'Yogur proteico con muesli sin azúcar y arándanos frescos.',
    ['midmorning', 'afternoon_snack', 'extra'], 'dairy', 3,
    ['quick', 'no_cook', 'high_protein'],
    [
      'Pon el yogur en un bol y añade el muesli justo antes de comer, para que no se reblandezca.',
      'Reparte los arándanos por encima y remueve una vez.',
    ],
    [I('yogur-proteico', 175), I('muesli-sin-azucar', 45), I('arandanos', 80)]),

  R('bocadillo-de-tortilla-francesa', 'Bocadillo de tortilla francesa',
    'Pan con una tortilla francesa recién hecha y tomate en rodajas.',
    ['midmorning', 'afternoon_snack', 'extra'], 'egg', 8,
    ['spanish', 'quick', 'high_protein'],
    [
      'Bate los huevos con sal y cuájalos en la sartén con el aceite, doblando la tortilla al tamaño del pan.',
      'Abre el pan, coloca la tortilla dentro y añade el tomate en rodajas finas.',
    ],
    [I('pan-integral', 90), I('huevo', 2, { unit: 'unit' }), I('aceite-oliva-virgen-extra', 6, { unit: 'ml' }), I('tomate', 40)]),

  R('wrap-de-pavo-y-queso-crema', 'Wrap de pavo con queso crema y canónigos',
    'Tortilla de trigo enrollada con queso crema, pavo y canónigos.',
    ['midmorning', 'afternoon_snack', 'extra'], 'poultry', 5,
    ['quick', 'no_cook', 'high_protein'],
    [
      'Extiende el queso crema sobre la tortilla de trigo, dejando un dedo libre en el borde.',
      'Reparte el pavo y los canónigos, enrolla apretando y córtalo por la mitad en diagonal.',
    ],
    [I('tortilla-trigo', 80), I('pavo-fiambre', 70), I('queso-crema-light', 30), I('canonigos', 25)]),

  R('muesli-con-leche-y-platano', 'Muesli con leche y plátano',
    'Un bol de muesli sin azúcar con leche fría y plátano en rodajas.',
    ['midmorning', 'afternoon_snack', 'extra'], 'dairy', 3,
    ['quick', 'no_cook'],
    [
      'Pon el muesli en un bol y vierte la leche por encima.',
      'Añade el plátano en rodajas y deja reposar dos minutos para que los copos se ablanden.',
    ],
    [I('muesli-sin-azucar', 70), I('leche-semidesnatada', 250, { unit: 'ml' }), I('platano', 110)]),

  // ----------------------------------------------------------- comidas
  R('lentejas-estofadas-con-verduras', 'Lentejas estofadas con sofrito de verduras y pimentón',
    'Un guiso de lentejas con sofrito de cebolla, pimiento y zanahoria, y punto de pimentón dulce.',
    ['lunch'], 'legume', 45,
    ['spanish', 'one_pot', 'vegetarian'],
    [
      'Pica la cebolla, el pimiento verde y la zanahoria en dados pequeños y póchalos en el aceite a fuego suave unos diez minutos, hasta que la cebolla esté transparente.',
      'Añade el tomate triturado y deja que se fría hasta que pierda el agua y se oscurezca.',
      'Retira del fuego, incorpora el pimentón y remueve rápido: si se quema, amarga todo el guiso.',
      'Añade las lentejas y el laurel, cubre con agua dos dedos por encima y cuece a fuego bajo unos treinta minutos, hasta que estén tiernas pero enteras.',
    ],
    [I('lentejas-secas', 100), I('zanahoria', 80), I('cebolla', 60), I('pimiento-verde', 50), I('tomate-triturado', 80), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('pimenton-dulce', 2, { fixed: true }), I('laurel', 0.5, { fixed: true })]),

  R('garbanzos-con-espinacas', 'Garbanzos con espinacas al ajo y pimentón',
    'El plato de vigilia de siempre: garbanzos con espinacas, ajo y pimentón, ligado con pan.',
    ['lunch'], 'legume', 40,
    ['spanish', 'one_pot', 'vegetarian'],
    [
      'Cuece los garbanzos hasta que estén tiernos y resérvalos con un poco de su caldo.',
      'Lamina el ajo y dóralo en el aceite. Aparta la sartén del fuego y añade el pimentón.',
      'Incorpora las espinacas y saltéalas hasta que reduzcan de volumen.',
      'Añade los garbanzos escurridos y el pan rallado, y remueve un par de minutos con un poco del caldo hasta que la salsa ligue.',
    ],
    [I('garbanzos-secos', 100), I('espinaca', 150), I('ajo', 5), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('pimenton-dulce', 2, { fixed: true }), I('pan-rallado', 10)]),

  R('arroz-con-pollo-y-verduras', 'Arroz con pollo, pimiento rojo y guisantes',
    'Arroz cocido en caldo con pollo dorado, pimiento rojo y guisantes.',
    ['lunch'], 'poultry', 35,
    ['spanish', 'one_pot', 'high_protein'],
    [
      'Corta la pechuga en dados, sálala y dórala en el aceite a fuego fuerte. Retírala en cuanto tome color.',
      'En la misma cazuela, sofríe la cebolla y el pimiento picados hasta que estén blandos.',
      'Añade el arroz y remueve un minuto para que se impregne, vierte el caldo caliente y devuelve el pollo.',
      'Añade los guisantes a mitad de cocción y deja hacer unos dieciocho minutos. Reposa cinco minutos tapado antes de servir.',
    ],
    [I('arroz-blanco', 90), I('pechuga-pollo', 180), I('pimiento-rojo', 80), I('guisantes-congelados', 60), I('cebolla', 50), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('caldo-pollo', 200, { unit: 'ml' })]),

  R('pasta-integral-con-atun', 'Pasta integral con atún, tomate y orégano',
    'Pasta integral con una salsa rápida de tomate y cebolla, atún y orégano.',
    ['lunch'], 'fish', 25,
    ['mediterranean', 'quick', 'high_protein'],
    [
      'Pon a cocer la pasta en agua con sal y calcula el tiempo del paquete.',
      'Mientras, pocha la cebolla picada en el aceite y añade el tomate triturado. Cuece diez minutos a fuego medio para que reduzca.',
      'Escurre la pasta reservando un poco del agua y añade el atún escurrido a la salsa.',
      'Mezcla la pasta con la salsa, aflojando con el agua de cocción si hace falta, y termina con el orégano.',
    ],
    [I('pasta-integral', 90), I('atun-lata-natural', 100), I('tomate-triturado', 120), I('cebolla', 50), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('oregano', 1, { fixed: true })]),

  R('merluza-al-horno-con-patatas', 'Merluza al horno sobre patatas panadera',
    'Lomo de merluza horneado sobre una cama de patata y cebolla, con aceite y limón.',
    ['lunch', 'dinner'], 'fish', 40,
    ['spanish', 'oven', 'high_protein'],
    [
      'Precalienta el horno a 200 °C. Corta la patata y la cebolla en rodajas finas, sálalas y extiéndelas en la bandeja con parte del aceite.',
      'Hornea las patatas veinte minutos, hasta que empiecen a estar tiernas.',
      'Coloca la merluza encima, riega con el resto del aceite y el zumo de limón, y hornea diez minutos más.',
      'Comprueba el pescado: tiene que separarse en lascas sin resistencia. Si se pasa, queda seco.',
    ],
    [I('merluza', 200), I('patata', 250), I('cebolla', 60), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('limon', 20)]),

  R('pollo-al-curry-con-arroz-integral', 'Pollo al curry con arroz integral y guisantes',
    'Pollo guisado con cebolla y curry en una salsa ligera, con arroz integral al lado.',
    ['lunch'], 'poultry', 35,
    ['one_pot', 'high_protein'],
    [
      'Pon a cocer el arroz integral: tarda unos veinticinco minutos, así que empieza por ahí.',
      'Dora el pollo en dados con el aceite y retíralo. Sofríe la cebolla picada en la misma sartén.',
      'Añade el curry a la cebolla y tuéstalo treinta segundos, hasta que huela; así deja de saber a polvo.',
      'Vierte la bebida de soja, devuelve el pollo, añade los guisantes y cuece diez minutos a fuego suave hasta que espese. Sirve con el arroz.',
    ],
    [I('pechuga-pollo', 180), I('arroz-integral', 90), I('cebolla', 60), I('bebida-soja', 80, { unit: 'ml' }), I('curry', 4, { fixed: true }), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('guisantes-congelados', 50)]),

  R('ternera-guisada-con-verduras', 'Ternera guisada con patata y zanahoria',
    'Un guiso lento de ternera con zanahoria y patata en salsa de tomate y caldo.',
    ['lunch'], 'red_meat', 60,
    ['spanish', 'one_pot'],
    [
      'Corta la ternera en dados grandes, sálala y séllala a fuego fuerte en el aceite, por tandas, hasta que esté dorada por fuera. Retírala.',
      'Baja el fuego y pocha la cebolla picada. Añade el tomate triturado y sofríe cinco minutos.',
      'Devuelve la carne, añade la zanahoria en rodajas y el caldo, y guisa tapado a fuego muy bajo cuarenta minutos.',
      'Incorpora la patata chascada en trozos irregulares, para que suelte almidón y ligue la salsa, y cuece veinte minutos más.',
    ],
    [I('ternera-filete-magro', 170), I('zanahoria', 100), I('patata', 200), I('cebolla', 60), I('tomate-triturado', 60), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('caldo-verduras', 200, { unit: 'ml' })]),

  R('alubias-con-verduras', 'Alubias blancas con puerro, zanahoria y calabacín',
    'Alubias blancas guisadas con verdura, terminadas con aceite crudo y pimentón.',
    ['lunch'], 'legume', 50,
    ['spanish', 'one_pot', 'vegetarian'],
    [
      'Pon las alubias a cocer en agua fría con el puerro y la zanahoria en trozos grandes.',
      'Cuando rompa a hervir, baja el fuego al mínimo: si borbotean fuerte se les cae la piel.',
      'A los cuarenta minutos añade el calabacín en dados y cuece diez minutos más, hasta que la alubia esté cremosa.',
      'Fuera del fuego, añade el aceite crudo y el pimentón, y menea la cazuela para que ligue.',
    ],
    [I('alubias-blancas-secas', 100), I('puerro', 60), I('zanahoria', 70), I('calabacin', 80), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('pimenton-dulce', 2, { fixed: true })]),

  R('salmon-con-quinoa-y-brocoli', 'Salmón a la plancha con quinoa y brócoli al vapor',
    'Salmón marcado a la plancha con quinoa esponjada y brócoli al vapor con limón.',
    ['lunch', 'dinner'], 'fish', 30,
    ['mediterranean', 'high_protein'],
    [
      'Enjuaga la quinoa bajo el grifo para quitarle el amargor y cuécela quince minutos en el doble de agua.',
      'Cuece el brócoli al vapor cinco minutos: tiene que quedar verde vivo y firme.',
      'Marca el salmón en la sartén con el aceite, primero por el lado de la piel tres minutos y dos por el otro.',
      'Sirve la quinoa de base, el brócoli al lado y el salmón encima, con el limón exprimido por encima.',
    ],
    [I('salmon-fresco', 170), I('quinoa', 80), I('brocoli', 150), I('aceite-oliva-virgen-extra', 8, { unit: 'ml' }), I('limon', 15)]),

  R('cuscus-con-pollo-y-verduras', 'Cuscús con pollo especiado al comino y verduras salteadas',
    'Cuscús hidratado con pollo al comino y calabacín, pimiento y zanahoria salteados.',
    ['lunch'], 'poultry', 25,
    ['mediterranean', 'quick', 'high_protein'],
    [
      'Corta el pollo en tiras, sálalo y mézclalo con el comino.',
      'Saltéalo a fuego fuerte en el aceite hasta que se dore, y añade el calabacín, el pimiento y la zanahoria en tiras. Cocina cinco minutos más para que queden al dente.',
      'Hidrata el cuscús con el mismo volumen de agua hirviendo y sal, tápalo y deja reposar cinco minutos.',
      'Suelta el cuscús con un tenedor y mézclalo con el salteado.',
    ],
    [I('cuscus', 90), I('pechuga-pollo', 170), I('calabacin', 100), I('pimiento-rojo', 80), I('zanahoria', 60), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('comino', 2, { fixed: true })]),

  R('lentejas-con-arroz-al-curry', 'Lentejas con arroz al curry',
    'Lentejas y arroz cocidos juntos con cebolla, zanahoria y curry.',
    ['lunch'], 'legume', 40,
    ['one_pot', 'vegetarian'],
    [
      'Pocha la cebolla y la zanahoria picadas en el aceite, cinco minutos.',
      'Añade las lentejas, cubre con agua y cuece veinte minutos.',
      'Incorpora el curry y el arroz, ajusta el agua y termina la cocción junta unos quince minutos, hasta que el arroz esté hecho y quede jugoso.',
    ],
    [I('lentejas-secas', 90), I('arroz-blanco', 50), I('cebolla', 60), I('zanahoria', 60), I('curry', 3, { fixed: true }), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' })]),

  R('paella-de-marisco', 'Paella de marisco con gambas, calamar y mejillones',
    'Arroz seco con sofrito de pimiento y tomate, gambas, calamar y mejillones.',
    ['lunch'], 'shellfish', 45,
    ['spanish', 'one_pot'],
    [
      'Saltea las gambas y el calamar treinta segundos en el aceite y retíralos: se terminarán de hacer al final.',
      'Sofríe el pimiento rojo picado y añade el tomate triturado; deja que se fría hasta que oscurezca.',
      'Añade el arroz, remueve un minuto y vierte el caldo caliente. Cuece a fuego fuerte cinco minutos y baja el fuego.',
      'A los diez minutos coloca el marisco y los mejillones por encima. Termina la cocción y deja reposar cinco minutos fuera del fuego, tapado con un paño.',
    ],
    [I('arroz-bomba', 100), I('gambas', 100), I('calamar', 100), I('mejillones', 120), I('pimiento-rojo', 60), I('tomate-triturado', 60), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('caldo-pollo', 250, { unit: 'ml' })]),

  R('pavo-con-boniato-al-horno', 'Pavo guisado con boniato y zanahoria',
    'Pechuga de pavo guisada con boniato, que se deshace y espesa la salsa.',
    ['lunch'], 'poultry', 45,
    ['one_pot', 'high_protein'],
    [
      'Corta el pavo en dados, sálalo y dóralo en el aceite. Retíralo.',
      'Pocha la cebolla y la zanahoria en la misma cazuela hasta que estén blandas.',
      'Añade el boniato en dados, devuelve el pavo y cubre con el caldo.',
      'Guisa tapado a fuego bajo veinticinco minutos, hasta que el boniato se deshaga al presionarlo y la salsa esté espesa.',
    ],
    [I('pechuga-pavo', 180), I('boniato', 220), I('cebolla', 60), I('zanahoria', 70), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('caldo-verduras', 200, { unit: 'ml' })]),

  R('bacalao-con-garbanzos', 'Bacalao sobre garbanzos con espinacas al ajo',
    'Lomo de bacalao hecho al vapor sobre un guiso rápido de garbanzos y espinacas.',
    ['lunch'], 'fish', 30,
    ['spanish', 'high_protein'],
    [
      'Lamina el ajo y dóralo en el aceite a fuego suave, sin que se queme.',
      'Añade las espinacas y saltéalas hasta que reduzcan; incorpora los garbanzos cocidos y escurridos.',
      'Coloca el bacalao encima con la piel hacia arriba, tapa la sartén y deja ocho minutos a fuego bajo: se hace al vapor y queda en lascas.',
    ],
    [I('bacalao-desalado', 170), I('garbanzos-cocidos', 200), I('espinaca', 100), I('ajo', 5), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' })]),

  R('hamburguesa-con-patatas-al-horno', 'Hamburguesa de ternera con patatas al horno',
    'Hamburguesa a la plancha en pan con tomate y lechuga, con patatas al horno.',
    ['lunch'], 'red_meat', 30,
    ['oven', 'quick'],
    [
      'Hornea las patatas a 210 °C unos veinte minutos, dándoles la vuelta a mitad para que doren por igual.',
      'Marca la hamburguesa a fuego fuerte, tres minutos por cada lado, y sálala al sacarla.',
      'Tuesta el pan por dentro en la misma sartén y monta con el tomate en rodajas y la lechuga.',
    ],
    [I('hamburguesa-ternera', 180), I('patatas-congeladas-horno', 200), I('tomate', 60), I('lechuga', 30), I('pan-molde-integral', 60)]),

  R('arroz-integral-con-tofu', 'Arroz integral con tofu dorado, brócoli y sésamo',
    'Tofu dorado y verduras salteadas con soja, sobre arroz integral y con sésamo.',
    ['lunch', 'dinner'], 'legume', 30,
    ['vegetarian', 'high_protein'],
    [
      'Cuece el arroz integral, que tarda unos veinticinco minutos.',
      'Escurre bien el tofu y sécalo con papel: si le queda agua no dora. Córtalo en dados y dóralo en el aceite por todas sus caras.',
      'Añade el brócoli y la zanahoria y saltea cinco minutos a fuego fuerte; incorpora la salsa de soja al final.',
      'Sirve sobre el arroz y espolvorea el sésamo.',
    ],
    [I('tofu-firme', 200), I('arroz-integral', 90), I('brocoli', 120), I('zanahoria', 70), I('salsa-soja', 15, { unit: 'ml' }), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('semillas-sesamo', 8)]),

  R('macarrones-con-carne-picada', 'Macarrones con boloñesa de ternera y orégano',
    'Macarrones con una boloñesa sencilla de ternera picada y tomate.',
    ['lunch'], 'red_meat', 30,
    ['mediterranean', 'quick'],
    [
      'Pocha la cebolla picada en el aceite hasta que esté transparente.',
      'Sube el fuego, añade la carne picada y deshazla con la cuchara hasta que pierda el color rojo.',
      'Añade el tomate triturado y cuece quince minutos a fuego medio, hasta que la salsa deje de estar acuosa.',
      'Cuece la pasta al dente, mézclala con la salsa fuera del fuego y termina con el orégano.',
    ],
    [I('pasta-seca', 95), I('ternera-picada-5', 150), I('tomate-triturado', 150), I('cebolla', 60), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('oregano', 1, { fixed: true })]),

  R('pollo-al-horno-con-verduras', 'Contramuslo de pollo al horno con verduras mediterráneas',
    'Contramuslo asado sobre calabacín, berenjena, pimiento y patata al orégano.',
    ['lunch'], 'poultry', 50,
    ['mediterranean', 'oven'],
    [
      'Precalienta el horno a 190 °C. Corta las verduras y la patata en trozos parecidos para que se hagan a la vez.',
      'Extiéndelas en la bandeja, salpimenta, riega con la mitad del aceite y espolvorea el orégano.',
      'Coloca el contramuslo encima con la piel hacia arriba y riega con el resto del aceite.',
      'Hornea cuarenta minutos. Si la piel no está dorada al final, sube a 220 °C los últimos cinco.',
    ],
    [I('contramuslo-pollo', 200), I('calabacin', 120), I('berenjena', 120), I('pimiento-rojo', 100), I('patata', 150), I('aceite-oliva-virgen-extra', 14, { unit: 'ml' }), I('oregano', 1, { fixed: true })]),

  R('atun-con-ensalada-de-patata', 'Atún a la plancha con ensalada templada de patata y judías',
    'Lomo de atún marcado con una ensalada templada de patata, judías verdes y huevo.',
    ['lunch'], 'fish', 30,
    ['spanish', 'high_protein'],
    [
      'Cuece la patata en dados y las judías verdes en la misma agua; añade el huevo diez minutos antes de terminar.',
      'Escurre, pela el huevo y córtalo en cuartos. Aliña patata y judías en templado con el aceite, el vinagre y sal.',
      'Marca el atún en la plancha muy caliente, un minuto por cada lado: por dentro tiene que quedar rosado.',
    ],
    [I('atun-fresco', 180), I('patata', 220), I('judias-verdes', 120), I('huevo', 1, { unit: 'unit' }), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('vinagre-vino', 10, { unit: 'ml' })]),

  R('sepia-con-arroz', 'Sepia con arroz y sofrito de pimiento',
    'Arroz cocido con sepia en un sofrito de cebolla, pimiento y tomate.',
    ['lunch'], 'shellfish', 35,
    ['spanish', 'one_pot'],
    [
      'Corta la sepia en tiras y séllala a fuego fuerte en el aceite. Retírala.',
      'Sofríe la cebolla y el pimiento verde picados hasta que estén blandos, y añade el tomate triturado.',
      'Incorpora el arroz, rehógalo un minuto y cubre con agua o caldo caliente.',
      'Devuelve la sepia a media cocción y termina los dieciocho minutos. Reposa cinco minutos antes de servir.',
    ],
    [I('sepia', 200), I('arroz-blanco', 90), I('pimiento-verde', 70), I('cebolla', 60), I('tomate-triturado', 60), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' })]),

  R('ensalada-de-lentejas-y-huevo', 'Ensalada de lentejas con huevo cocido y cebolleta',
    'Lentejas cocidas frías con tomate cherry, cebolleta y huevo cocido, aliñadas con módena.',
    ['lunch', 'dinner'], 'legume', 15,
    ['quick', 'no_cook', 'vegetarian'],
    [
      'Cuece los huevos diez minutos desde que hierve el agua y enfríalos bajo el grifo para pelarlos sin pelearte.',
      'Escurre y enjuaga las lentejas cocidas para quitarles el líquido de conserva.',
      'Mézclalas con el tomate cherry partido por la mitad y la cebolleta picada muy fina.',
      'Aliña con el aceite, el vinagre de módena y sal, y coloca el huevo en cuartos por encima.',
    ],
    [I('lentejas-cocidas', 250), I('huevo', 2, { unit: 'unit' }), I('tomate-cherry', 100), I('cebolleta', 30), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('vinagre-modena', 8, { unit: 'ml' })]),

  R('solomillo-de-cerdo-con-pure', 'Solomillo de cerdo con puré de patata y judías verdes',
    'Medallones de solomillo a la plancha con puré casero y judías verdes.',
    ['lunch'], 'pork', 35,
    ['spanish', 'high_protein'],
    [
      'Cuece la patata en trozos hasta que se atraviese sin resistencia, y las judías verdes aparte.',
      'Escurre la patata, aplástala y trabájala con la leche caliente y la mitad del aceite hasta que quede fina.',
      'Corta el solomillo en medallones gruesos y márcalos a fuego fuerte, dos minutos por cada lado. Sálalos al sacarlos y déjalos reposar dos minutos.',
    ],
    [I('solomillo-cerdo', 180), I('patata', 250), I('leche-semidesnatada', 60, { unit: 'ml' }), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('judias-verdes', 150)]),

  // ------------------------------------------------------------- cenas
  R('tortilla-de-patata-con-ensalada', 'Tortilla de patata jugosa con ensalada aliñada',
    'Tortilla de patata y cebolla confitadas, con ensalada de lechuga y tomate.',
    ['dinner'], 'egg', 35,
    ['spanish'],
    [
      'Corta la patata en láminas finas y la cebolla en juliana. Confítalas en el aceite a fuego bajo unos veinte minutos, sin que lleguen a dorarse.',
      'Escurre el aceite sobrante y mezcla la patata caliente con el huevo batido y sal. Deja reposar cinco minutos.',
      'Cuaja la tortilla a fuego medio dos minutos por cada lado, dándole la vuelta con un plato. Por dentro tiene que quedar jugosa.',
      'Aliña la lechuga y el tomate y sirve al lado.',
    ],
    [I('huevo', 3, { unit: 'unit' }), I('patata', 200), I('cebolla', 50), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('lechuga', 60), I('tomate', 80)]),

  R('crema-de-calabaza-con-pollo', 'Crema de calabaza y puerro con pollo a la plancha',
    'Crema suave de calabaza, patata y puerro, con pechuga a la plancha para acompañar.',
    ['dinner'], 'poultry', 35,
    ['spanish', 'one_pot'],
    [
      'Pocha el puerro picado en la mitad del aceite hasta que esté blando, sin dorar.',
      'Añade la calabaza y la patata en dados, cubre justo con agua y cuece veinte minutos.',
      'Tritura hasta que quede fina y ajusta de sal; si queda espesa, aflójala con el caldo de la cocción.',
      'Marca la pechuga a la plancha con el resto del aceite, cuatro minutos por cada lado, y córtala en tiras sobre la crema.',
    ],
    [I('calabaza', 300), I('patata', 80), I('puerro', 60), I('aceite-oliva-virgen-extra', 8, { unit: 'ml' }), I('pechuga-pollo', 160)]),

  R('merluza-en-salsa-verde', 'Merluza en salsa verde con espárragos',
    'Merluza cocida en su propia salsa verde ligada con harina, con espárragos verdes.',
    ['dinner'], 'fish', 30,
    ['spanish', 'high_protein'],
    [
      'Lamina el ajo y dóralo en el aceite en una cazuela ancha, a fuego suave.',
      'Añade la harina y tuéstala treinta segundos removiendo; vierte el caldo poco a poco sin dejar de mover.',
      'Coloca la merluza con la piel hacia abajo y los espárragos alrededor.',
      'Cuece a fuego muy suave ocho minutos moviendo la cazuela en vaivén: ese movimiento es lo que liga la salsa.',
    ],
    [I('merluza', 200), I('esparragos-verdes', 150), I('ajo', 5), I('harina-trigo', 8), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('caldo-verduras', 120, { unit: 'ml' })]),

  R('revuelto-de-gambas-y-esparragos', 'Revuelto de gambas y espárragos verdes',
    'Espárragos y gambas salteados con huevo cuajado al punto, con pan.',
    ['dinner'], 'shellfish', 15,
    ['spanish', 'quick', 'high_protein'],
    [
      'Trocea los espárragos en bastoncitos y saltéalos en el aceite cinco minutos, hasta que estén tiernos.',
      'Añade las gambas y saltea un minuto más, lo justo para que cambien de color.',
      'Baja el fuego, añade el huevo batido y remueve sin parar hasta que cuaje pero siga cremoso.',
      'Sirve con el pan tostado.',
    ],
    [I('huevo', 3, { unit: 'unit' }), I('gambas', 120), I('esparragos-verdes', 120), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('pan-integral', 40)]),

  R('ensalada-de-pollo-y-aguacate', 'Ensalada de pollo a la plancha con aguacate y nueces',
    'Hojas verdes con pollo templado, aguacate, tomate cherry y nueces.',
    ['dinner'], 'poultry', 15,
    ['mediterranean', 'quick', 'high_protein'],
    [
      'Salpimenta la pechuga y hazla a la plancha con parte del aceite, cuatro minutos por cada lado. Déjala reposar antes de cortarla en tiras.',
      'Mezcla la lechuga y los canónigos con el tomate cherry partido y el aguacate en gajos.',
      'Coloca el pollo templado encima, añade las nueces troceadas y aliña con el resto del aceite y sal.',
    ],
    [I('pechuga-pollo', 160), I('lechuga', 80), I('canonigos', 40), I('aguacate', 70), I('tomate-cherry', 100), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('nuez', 15)]),

  R('dorada-al-horno-con-verduras', 'Dorada al horno con patata, calabacín y limón',
    'Dorada entera al horno sobre patata y cebolla, con calabacín y limón.',
    ['dinner'], 'fish', 40,
    ['spanish', 'oven', 'high_protein'],
    [
      'Precalienta el horno a 200 °C. Extiende la patata y la cebolla en rodajas finas con parte del aceite y hornea veinte minutos.',
      'Añade el calabacín en rodajas y coloca la dorada encima, con dos cortes en el lomo para que se haga por dentro.',
      'Riega con el resto del aceite y el limón, y hornea quince minutos más.',
    ],
    [I('dorada', 220), I('calabacin', 120), I('cebolla', 60), I('patata', 150), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('limon', 20)]),

  R('wok-de-pavo-y-verduras', 'Wok de pavo con brócoli, pimiento y sésamo',
    'Salteado rápido a fuego fuerte de pavo y verduras con soja y sésamo.',
    ['dinner'], 'poultry', 20,
    ['quick', 'high_protein'],
    [
      'Corta el pavo en tiras finas y las verduras en bastones: en un salteado todo tiene que tener el mismo grosor.',
      'Calienta el aceite hasta que humee y saltea el pavo dos minutos sin moverlo demasiado. Retíralo.',
      'Saltea el brócoli, el pimiento y la zanahoria cuatro minutos: tienen que quedar crujientes.',
      'Devuelve el pavo, añade la salsa de soja, remueve treinta segundos y termina con el sésamo fuera del fuego.',
    ],
    [I('pechuga-pavo', 180), I('brocoli', 120), I('pimiento-rojo', 80), I('zanahoria', 70), I('salsa-soja', 15, { unit: 'ml' }), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('semillas-sesamo', 8)]),

  R('crema-de-verduras-con-tortilla', 'Crema de calabacín y puerro con tortilla francesa',
    'Crema ligera de verduras con una tortilla francesa jugosa al lado.',
    ['dinner'], 'egg', 30,
    ['spanish', 'one_pot'],
    [
      'Pocha el puerro y la zanahoria picados en la mitad del aceite, cinco minutos.',
      'Añade el calabacín y la patata en dados, cubre justo con agua y cuece veinte minutos.',
      'Tritura hasta que quede fina y ajusta de sal.',
      'Bate los huevos con sal y cuaja la tortilla francesa a fuego medio con el resto del aceite, doblándola en tres.',
    ],
    [I('calabacin', 200), I('puerro', 80), I('zanahoria', 80), I('patata', 80), I('aceite-oliva-virgen-extra', 8, { unit: 'ml' }), I('huevo', 3, { unit: 'unit' })]),

  R('ensalada-de-garbanzos-y-atun', 'Ensalada de garbanzos con atún y verduras picadas',
    'Garbanzos cocidos con atún, tomate, cebolleta y pimiento, todo en frío.',
    ['dinner', 'lunch'], 'legume', 12,
    ['spanish', 'quick', 'no_cook'],
    [
      'Escurre y enjuaga los garbanzos cocidos para quitarles el líquido de conserva.',
      'Pica el tomate, la cebolleta y el pimiento verde en dados pequeños y mézclalos con los garbanzos.',
      'Añade el atún escurrido y desmenuzado, aliña con el aceite y sal, y deja reposar diez minutos antes de comer.',
    ],
    [I('garbanzos-cocidos', 220), I('atun-lata-natural', 80), I('tomate', 100), I('cebolleta', 30), I('pimiento-verde', 50), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' })]),

  R('salmon-al-horno-con-brocoli', 'Salmón al horno con brócoli y patata',
    'Salmón horneado con patata y brócoli, terminado con aceite y limón.',
    ['dinner'], 'fish', 35,
    ['oven', 'high_protein'],
    [
      'Precalienta el horno a 200 °C y hornea la patata en rodajas quince minutos con parte del aceite.',
      'Añade el brócoli en ramilletes y el salmón, y hornea doce minutos más.',
      'Saca en cuanto el salmón se abra en lascas al presionarlo y riega con el resto del aceite y el limón.',
    ],
    [I('salmon-fresco', 180), I('brocoli', 200), I('patata', 150), I('aceite-oliva-virgen-extra', 8, { unit: 'ml' }), I('limon', 20)]),

  R('pollo-con-ensalada-de-quinoa', 'Pollo a la plancha con ensalada de quinoa, tomate y pepino',
    'Quinoa templada con tomate cherry y pepino, con pechuga a la plancha y limón.',
    ['dinner'], 'poultry', 25,
    ['mediterranean', 'high_protein'],
    [
      'Enjuaga la quinoa y cuécela quince minutos. Escúrrela y extiéndela en un plato para que temple rápido.',
      'Mézclala con el tomate cherry partido y el pepino en dados.',
      'Haz la pechuga a la plancha con el aceite, cuatro minutos por cada lado, y córtala en tiras.',
      'Aliña con el zumo de limón y sal, y sirve el pollo encima.',
    ],
    [I('pechuga-pollo', 170), I('quinoa', 70), I('tomate-cherry', 100), I('pepino', 80), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('limon', 15)]),

  R('tortilla-de-champinones-y-queso', 'Tortilla de champiñones y queso semicurado',
    'Champiñones salteados dentro de una tortilla con queso fundido, con pan.',
    ['dinner'], 'egg', 20,
    ['spanish', 'quick'],
    [
      'Lamina los champiñones y saltéalos a fuego fuerte en el aceite hasta que suelten el agua y se doren.',
      'Bate los huevos con sal, añádelos a la sartén y reparte el queso rallado por encima.',
      'Cuaja a fuego medio y dobla la tortilla por la mitad cuando el queso empiece a fundirse.',
      'Sirve con el pan.',
    ],
    [I('huevo', 3, { unit: 'unit' }), I('champinon', 150), I('queso-semicurado', 30), I('aceite-oliva-virgen-extra', 8, { unit: 'ml' }), I('pan-integral', 50)]),

  R('lubina-con-menestra', 'Lubina a la plancha con menestra salteada al ajo',
    'Lomo de lubina a la plancha con menestra de verduras salteada con ajo.',
    ['dinner'], 'fish', 25,
    ['spanish', 'quick', 'high_protein'],
    [
      'Lamina el ajo y dóralo en parte del aceite; añade la menestra y saltéala hasta que esté tierna.',
      'Seca bien la lubina y hazla a la plancha muy caliente, primero por la piel tres minutos, hasta que quede crujiente.',
      'Dale la vuelta un minuto, sálala al sacarla y sírvela sobre la menestra.',
    ],
    [I('lubina', 220), I('menestra-congelada', 250), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('ajo', 5)]),

  R('sopa-de-fideos-con-pollo', 'Sopa de fideos con pollo y verduras',
    'Caldo con zanahoria y puerro, pollo desmenuzado y fideos finos.',
    ['dinner'], 'poultry', 25,
    ['spanish', 'one_pot'],
    [
      'Lleva el caldo a ebullición con la zanahoria y el puerro cortados en rodajas finas y cuece diez minutos.',
      'Añade la pechuga entera y cuécela ocho minutos; sácala, desmenúzala con dos tenedores y devuélvela.',
      'Echa los fideos y cuécelos los minutos que diga el paquete: pasados, se deshacen y espesan la sopa.',
    ],
    [I('caldo-pollo', 400, { unit: 'ml' }), I('fideos-finos', 60), I('pechuga-pollo', 150), I('zanahoria', 60), I('puerro', 50)]),

  R('berenjenas-rellenas-de-carne', 'Berenjenas rellenas de carne gratinadas con mozzarella',
    'Berenjenas asadas y rellenas de carne con sofrito, gratinadas con mozzarella.',
    ['dinner'], 'red_meat', 45,
    ['mediterranean', 'oven'],
    [
      'Parte las berenjenas a lo largo, haz cortes en la carne y ásalas veinticinco minutos a 200 °C.',
      'Vacíalas con una cuchara sin romper la piel y pica la pulpa.',
      'Pocha la cebolla en el aceite, añade la carne picada y dórala; incorpora la pulpa y el tomate triturado y cuece diez minutos.',
      'Rellena las pieles, cubre con la mozzarella y gratina cinco minutos hasta que burbujee.',
    ],
    [I('berenjena', 300), I('ternera-picada-5', 150), I('cebolla', 60), I('tomate-triturado', 80), I('mozzarella', 40), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' })]),

  R('ensalada-de-mozzarella-y-pavo', 'Ensalada de tomate, mozzarella y pavo con rúcula',
    'Tomate y mozzarella alternados con pavo y rúcula, aliñados con aceite y módena.',
    ['dinner'], 'dairy', 10,
    ['mediterranean', 'quick', 'no_cook'],
    [
      'Corta el tomate y la mozzarella en rodajas del mismo grosor y altérnalas en el plato.',
      'Sala el tomate un par de minutos antes de servir: saca su jugo y ese jugo se mezcla con el aliño.',
      'Añade el pavo en tiras y la rúcula por encima, y aliña con el aceite y el vinagre de módena. Sirve con el pan.',
    ],
    [I('mozzarella', 100), I('tomate', 200), I('pavo-fiambre', 80), I('rucula', 40), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('vinagre-modena', 8, { unit: 'ml' }), I('pan-integral', 50)]),

  R('calamares-con-ensalada', 'Calamares a la plancha con ensalada y limón',
    'Calamar a la plancha con limón y una ensalada sencilla de lechuga, tomate y pimiento.',
    ['dinner'], 'shellfish', 20,
    ['spanish', 'quick', 'high_protein'],
    [
      'Seca muy bien el calamar y ábrelo en tiras anchas.',
      'Hazlo en la plancha muy caliente con parte del aceite, dos minutos por cada lado: pasado de tiempo se vuelve gomoso.',
      'Aliña la lechuga, el tomate y el pimiento verde con el resto del aceite y sal, y sirve el calamar con el limón exprimido.',
    ],
    [I('calamar', 220), I('lechuga', 80), I('tomate', 100), I('pimiento-verde', 50), I('aceite-oliva-virgen-extra', 12, { unit: 'ml' }), I('limon', 20)]),

  R('crema-de-lentejas-y-calabaza', 'Crema de lentejas y calabaza al comino',
    'Lentejas y calabaza cocidas y trituradas, con comino y aceite crudo.',
    ['dinner'], 'legume', 35,
    ['one_pot', 'vegetarian'],
    [
      'Pocha la cebolla y la zanahoria picadas en la mitad del aceite.',
      'Añade las lentejas y la calabaza en dados, cubre con agua y cuece treinta minutos, hasta que la lenteja se deshaga.',
      'Tritura hasta que quede una crema fina y añade el comino.',
      'Termina cada plato con el aceite crudo restante en hilo.',
    ],
    [I('lentejas-secas', 90), I('zanahoria', 80), I('cebolla', 50), I('calabaza', 120), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' }), I('comino', 2, { fixed: true })]),

  R('tosta-de-sardinas-y-tomate', 'Tosta de sardinas con tomate y rúcula',
    'Pan tostado con tomate, sardinas en aceite escurridas y rúcula.',
    ['dinner'], 'fish', 10,
    ['spanish', 'quick'],
    [
      'Tuesta el pan hasta que quede firme: las sardinas pesan y el pan blando se hunde.',
      'Ralla el tomate, escúrrelo y extiéndelo sobre el pan con un hilo de aceite.',
      'Escurre bien las sardinas, colócalas encima y termina con la rúcula.',
    ],
    [I('pan-integral', 80), I('sardinas-lata-oliva', 90), I('tomate', 100), I('aceite-oliva-virgen-extra', 5, { unit: 'ml' }), I('rucula', 20)]),

  R('revuelto-de-tofu-con-verduras', 'Revuelto de tofu con calabacín, champiñón y soja',
    'Tofu desmenuzado salteado con verduras y salsa de soja, con textura de revuelto.',
    ['dinner'], 'legume', 20,
    ['vegetarian', 'quick', 'high_protein'],
    [
      'Escurre el tofu, sécalo con papel y desmenúzalo con las manos hasta que parezca huevo revuelto.',
      'Saltea el champiñón laminado y el calabacín y el pimiento en dados a fuego fuerte, cinco minutos.',
      'Añade el tofu y saltea otros cinco minutos, sin removerlo mucho, para que se dore por algunas caras.',
      'Incorpora la salsa de soja al final y remueve treinta segundos.',
    ],
    [I('tofu-firme', 200), I('calabacin', 120), I('champinon', 100), I('pimiento-rojo', 70), I('salsa-soja', 12, { unit: 'ml' }), I('aceite-oliva-virgen-extra', 10, { unit: 'ml' })]),
];

const POR_SLUG = new Map(RECIPES.map((recipe) => [recipe.slug, recipe]));

export function findRecipe(slug: string): Recipe | undefined {
  return POR_SLUG.get(slug);
}

export function requireRecipe(slug: string): Recipe {
  const recipe = POR_SLUG.get(slug);
  if (!recipe) throw new Error(`Receta desconocida: ${slug}`);
  return recipe;
}

export function recipesForSlot(slot: MealSlot): Recipe[] {
  return RECIPES.filter((recipe) => recipe.slots.includes(slot));
}
